import { useDialogState, useLoadingState } from 'hooks'
import { OutboundTripNeedsDriversLicensePhotoUploadedPhotoProps } from './index'
import { useState } from 'react'
import * as controllers from 'controllers'
import { enqueueSnackbar } from 'notistack'
import { Button, Card, CardContent, Divider, Grid } from '@mui/material'
import { NeedsClusterRemovedDetailsDto, NeedsClusterRemovedPulledClusterPhotoDto, VehicleDto } from 'dtos'
import { ArrowRight, ArrowLeft } from '@mui/icons-material'
import { ExplanationAccordion, GreenText } from 'components'
import { NeedsDriversLicensePhotoUploadedDetailsDto } from 'dtos/needsDriversLicensePhotoUploadedDetailsDto'
import DriversLicensePhotoDialog from './DriversLicensePhotoDialog'
import NeedsDriversLicensePhotoUploadedStepper from './NeedsDriversLicensePhotoUploadedStepper'
import DriversLicensePhotoCard from './DriversLicensePhotoCard'
import { useNavigate } from 'react-router-dom'

interface Step1DriversLicensePhotoProps extends OutboundTripNeedsDriversLicensePhotoUploadedPhotoProps {
  onNext: () => void
  needsDriversLicensePhotoUploadedDetails: NeedsDriversLicensePhotoUploadedDetailsDto
  outboundTripGuid?: string
}

export default function Step1DriversLicensePhoto({
  onNext,
  needsDriversLicensePhotoUploadedDetails,
  outboundTripGuid,
  driversLicensePhoto,
  setDriversLicensePhoto
}: Step1DriversLicensePhotoProps) {
  const navigate = useNavigate()

  const [loadingState, setLoadingState] = useLoadingState({
    isUpdatingDriversLicensePhoto: false,
    isDeletingDriversLicensePhoto: false
  })

  const [dialogState, setDialogState] = useDialogState({
    driversLicensePhotoDialogOpen: false
  })

  const hasDriversLicensePhoto = Boolean(driversLicensePhoto.driversLicensePhotoFileName)

  return (
    <>
      <DriversLicensePhotoDialog
        initialValues={driversLicensePhoto}
        isLoading={loadingState.isDeletingDriversLicensePhoto || loadingState.isUpdatingDriversLicensePhoto}
        onClose={() => {
          setDialogState('driversLicensePhotoDialogOpen', false)
        }}
        onDelete={() => {
          setLoadingState('isDeletingDriversLicensePhoto', true)
          controllers.outboundTrips
            .deleteNeedsDriversLicensePhotoUploadedDriversLicensePhoto(outboundTripGuid!)
            .then(response => {
              enqueueSnackbar('Drivers License Photo Deleted Successfully', { variant: 'success' })
              setDriversLicensePhoto(response.value)
              setDialogState('driversLicensePhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isDeletingDriversLicensePhoto', false)
            })
        }}
        onSave={values => {
          setLoadingState('isUpdatingDriversLicensePhoto', true)
          controllers.outboundTrips
            .updateNeedsDriversLicensePhotoUploadedDriversLicensePhotoInformation(outboundTripGuid!, values)
            .then(response => {
              if (response?.value) {
                setDriversLicensePhoto(response.value)
                enqueueSnackbar('Drivers License Photo Updated Successfully', { variant: 'success' })
              } else {
                console.error('Unexpected API Response:', response)
              }
              setDialogState('driversLicensePhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isUpdatingDriversLicensePhoto', false)
            })
        }}
        open={dialogState.driversLicensePhotoDialogOpen}
        outboundTripGuid={outboundTripGuid!}
      />

      <Card>
        <CardContent>
          <Grid container spacing={1} alignItems='center'>
            <Grid item xs={12}>
              <NeedsDriversLicensePhotoUploadedStepper activeStep={1} />
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12}>
              <DriversLicensePhotoCard
                complete={Boolean(driversLicensePhoto.driversLicensePhotoFileName)}
                fileName={driversLicensePhoto.driversLicensePhotoFileName}
                onClick={() => {
                  setDialogState('driversLicensePhotoDialogOpen', true)
                }}
                type='drivers-license'
                outboundTripGuid={outboundTripGuid}
              />
            </Grid>

            <Grid item xs={12} marginTop={2}>
              <Button
                color='primary'
                endIcon={<ArrowRight />}
                fullWidth
                onClick={onNext}
                type='submit'
                variant='contained'
                disabled={!hasDriversLicensePhoto}
              >
                NEXT
              </Button>
            </Grid>

            <Grid item xs={12}>
              <Button
                color='primary'
                startIcon={<ArrowLeft />}
                fullWidth
                onClick={() => navigate('/to-do-list')}
                variant='outlined'
              >
                PREVIOUS
              </Button>
            </Grid>

            <Grid item xs={12}>
              <GreenText variant='outlined'>
                {`**To Do List Steps:**

Displays the current step of the for the To Do List in the Needs
Drivers License Uploaded Process.

**Drivers Licenses Photo:**

Displays the Drivers Licenses Photo image uploaded by the
user.

If no image has been uploaded, a “No Image” icon will be
displayed instead.

Clicking the Drivers License Photo will open the Capture Drivers
License Photo Photo Dialog.

**NEXT Button:**

Clicking this button will:

- Save the uploaded photo to the database.

- Advance the user to the next step in the process.

This button remains disabled until a photo has been uploaded.

**PREVIOUS Button:**

Clicking this button will:

- Discard any uploaded photo that has not been saved.

- Return the user to the previous step in the process.

          `}
              </GreenText>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
